import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useIsMutating, useQueryClient } from '@zg-rentals/react-query5';
import {
  AccountViewListingStatus,
  OptimizationMode,
  PlacementSource,
  RentalManagerUserPropertySpotlightWebApiHandler_list_Path,
  SmartConfigMode,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertySpotlightWebApiHandler_getOptimizationConfig,
  useRentalManagerUserPropertySpotlightWebApiHandler_list,
  useRentalManagerUserPropertySpotlightWebApiHandler_setOptimizationMode,
  useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useSpotlightOverview } from '../../../hooks/queries/properties/zram';
import propertyUtils from '../../../utils/propertyUtils';
import { useSpotlightBatchMutations } from './hooks/useSpotlightBatchMutations';
import { useSpotlightMutations } from './hooks/useSpotlightMutations';
import { type BulkSpotlightTarget, planBulkSpotlightTransitions } from './utils/bulkSpotlightTransitions';
import { dropFrom, isCurrentlySelectable, isManagedByFeedListing } from './utils/cacheHelpers';

import { SmartAssignDisableModal } from './SmartAssignDisableModal';
import { SpotlightReassignModal } from './SpotlightReassignModal';

import { type OptimizerId, RAB_LICENSES } from './rabDeployment';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

import type { ReactNode } from 'react';
import type { AccountViewListingDto } from '@zg-rentals/ts-rental-manager-api-client';

const FOR_RENT = new Set<AccountViewListingStatus>([
  AccountViewListingStatus.PUBLISHED,
  AccountViewListingStatus.PARTIALLY_PUBLISHED,
  AccountViewListingStatus.PUBLISHING,
  AccountViewListingStatus.EXPIRES_SOON,
  AccountViewListingStatus.LEASED,
]);

export type SpotlightOccupant = {
  alias: string;
  label: string;
  meta: string;
  origin: 'auto' | 'pinned';
};

export type AutoSpotlightContextValue = {
  active: boolean;
  applyBulkTarget: (ids: Array<string>, target: BulkSpotlightTarget) => void;
  batchExclude: (ids: Array<string>) => void;
  batchInclude: (ids: Array<string>) => void;
  batchPin: (ids: Array<string>) => void;
  batchUnexclude: (ids: Array<string>) => void;
  batchUninclude: (ids: Array<string>) => void;
  batchUnpin: (ids: Array<string>) => void;
  clearSelection: () => void;
  exclude: (id: string) => void;
  excludedIds: Set<string>;
  /** True once the spotlight overview API response has been received. Used to gate the license-limit check. */
  isOverviewLoaded: boolean;
  isSelectionMode: boolean;
  /** True when every currently-rendered (bulk-selectable) row is selected. */
  isAllOnPageSelected: boolean;
  selectedIds: Set<string>;
  toggleSelection: (id: string, placementSource: PlacementSource | null) => void;
  toggleSelectionMode: () => void;
  /** Selects/deselects every bulk-selectable row currently rendered -- current page only. */
  toggleSelectAllOnPage: () => void;
  /** Feed-managed (AppFolio/custom-feed) listings, including editable Smart Assign candidates. */
  managedByFeedIds: Set<string>;
  licenses: number;
  onChangeOptimizer: (id: OptimizerId) => void;
  optimizer: OptimizerId;
  /** The customer's Smart Assign mode. `null` means Smart Assign is off. */
  smartConfigMode: SmartConfigMode | null;
  isDisableSmartAssignRequested: boolean;
  isDisablingSmartAssign: boolean;
  requestDisableSmartAssign: () => void;
  cancelDisableSmartAssign: () => void;
  confirmDisableSmartAssign: () => void;
  /** Count of listings whose advertising placementSource is PINNED. */
  pinnedCount: number;
  /** Count of listings whose advertising placementSource is AUTO. */
  autoCount: number;
  /** Count of listings currently in Spotlight (from the overview). */
  spotlightedCount: number;
  /** Whether the customer has any ZRAM-manageable (non-feed-managed) listings. */
  hasZramManageableListings: boolean;
  hasBulkSelectableListings: boolean;
  /** Count of available spotlight placements. */
  availableCount: number;
  /** Per-listing package name (the plan the listing already pays for). Spotlight sits on top of it. */
  packageNameById: Record<string, string>;
  pinnedIds: Set<string>;
  pin: (id: string) => void;
  planBulkTarget: (ids: Array<string>, target: BulkSpotlightTarget) => ReturnType<typeof planBulkSpotlightTransitions>;
  reassignCandidate: string | null;
  reassignCandidateLabel: string;
  isReassignOccupantsLoading: boolean;
  reassignOccupants: Array<SpotlightOccupant>;
  cancelReassign: () => void;
  confirmReassign: (targetAlias: string) => void;
  setReassignCandidate: (id: string) => void;
  /** True from ~2s into a Pinned -> Smart Assign revert until the backend responds. See revertPinnedToSmartAssign. */
  isRevertingPinPending: boolean;
  isTableLocked: boolean;
  revertPinnedToSmartAssign: (id: string) => void;
  unexclude: (id: string) => void;
  unpin: (id: string) => void;
  vacantIds: Set<string>;
};

const AutoSpotlightContext = createContext<AutoSpotlightContextValue | null>(null);

export const useAutoSpotlight = () => useContext(AutoSpotlightContext);

export function AutoSpotlightProvider({
  active,
  children,
  initialCounts,
  initialExcludedIds = [],
  initialOptimizer = 'dom',
  initialPinnedIds = [],
  listings,
}: {
  active: boolean;
  children: ReactNode;
  initialCounts?: { pinnedCount: number; autoCount: number; availableCount: number };
  initialExcludedIds?: Array<string>;
  initialOptimizer?: OptimizerId;
  initialPinnedIds?: Array<string>;
  listings: Array<AccountViewListingDto>;
}) {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  // Locks the table while any spotlight change is in flight so nothing else can be selected or changed until the
  // result lands. Every spotlight mutation carries meta.context.api === 'spotlight', so this tracks single-cell and
  // bulk actions alike without each one having to flip a flag.
  const spotlightMutationCount = useIsMutating({
    predicate: (mutation) => mutation.options.meta?.context?.api === 'spotlight',
  });
  // The wait for the engine's optimization joblet is also a 'spotlight' mutation, so the lock holds until the table has
  // re-pulled the engine's result, not just until the change request returns.
  const isTableLocked = spotlightMutationCount > 0;
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set(initialExcludedIds));
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedPlacementSources, setSelectedPlacementSources] = useState<Map<string, PlacementSource | null>>(
    () => new Map(),
  );
  const [userOverride, setUserOverride] = useState<OptimizerId | null>(null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => new Set(initialPinnedIds));
  const [reassignCandidate, setReassignCandidate] = useState<string | null>(null);
  const [isDisableSmartAssignRequested, setIsDisableSmartAssignRequested] = useState(false);
  // RECE-2031: forces the table's existing skeleton view for the Pinned -> Smart Assign revert, which has no
  // optimistic count update (RECE-2014) and otherwise leaves a silent multi-second gap with no feedback. Keyed by
  // alias (not a single boolean) so two concurrent reverts don't clear each other's pending state early.
  const [revertingPinIds, setRevertingPinIds] = useState<Set<string>>(() => new Set());

  // Single source of truth for spotlight counts (initialized from server, managed optimistically)
  const [spotlightCounts, setSpotlightCounts] = useState<{
    pinnedCount: number;
    autoCount: number;
    availableCount: number;
  } | null>(initialCounts ?? null);

  // Tagged so setSmartConfigMode can invalidate it — without this the toggle stays on after a successful disable.
  const { data: configData } = useRentalManagerUserPropertySpotlightWebApiHandler_getOptimizationConfig({
    meta: {
      tags: ['optimizationConfig'],
      context: { api: 'getOptimizationConfig', details: 'spotlight optimizer mode and smart assign mode' },
    },
  });
  const { mutateAsync: saveOptimizationMode } = useRentalManagerUserPropertySpotlightWebApiHandler_setOptimizationMode({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'setOptimizationMode', details: 'spotlight optimizer mode' },
    },
  });

  const { mutateAsync: saveSmartConfigMode, isPending: isDisablingSmartAssign } =
    useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode({
      meta: {
        invalidates: ['getProperties', 'spotlightOverview', 'optimizationConfig'],
        context: { api: 'setSmartConfigMode', details: 'smart assign enablement' },
      },
    });

  const configOptimizer: OptimizerId = configData?.optimizationMode === OptimizationMode.RENT ? 'rent' : 'dom';
  const optimizer = userOverride ?? (configData ? configOptimizer : initialOptimizer);
  // The column is nullable even though the generated type is not, so normalize the off state to null.
  const smartConfigMode = configData?.smartConfigMode ?? null;

  // Real, account-wide Pinned / Auto-deployed totals from the spotlight overview endpoint (not paginated).
  const { data: overviewData, dataUpdatedAt: overviewDataUpdatedAt } = useSpotlightOverview();

  // Fetch all spotlighted listings (for reassign modal)
  // Only fetch when reassign modal is open to avoid unnecessary API calls
  const {
    data: spotlightedListings,
    isFetching: isReassignOccupantsFetching,
    isPending: isReassignOccupantsPending,
  } = useRentalManagerUserPropertySpotlightWebApiHandler_list({
    enabled: Boolean(reassignCandidate),
  });
  const isReassignOccupantsLoading =
    Boolean(reassignCandidate) &&
    spotlightedListings === undefined &&
    (isReassignOccupantsPending || isReassignOccupantsFetching);

  // Every fresh overview response is the source of truth — mutations invalidate it and the backend runs
  // the engine synchronously, so the refetched counts reflect the real post-re-rank state. The optimistic
  // deltas only bridge the invalidate→refetch window.
  useEffect(() => {
    if (overviewData) {
      setSpotlightCounts({
        pinnedCount: overviewData.pinnedCount ?? 0,
        autoCount: overviewData.autoCount ?? 0,
        availableCount: overviewData.availableCount ?? 0,
      });
    }
  }, [overviewData, overviewDataUpdatedAt]);

  const labelByAlias = useMemo(() => {
    const map: Record<string, { label: string; meta: string }> = {};
    listings.forEach((listing) => {
      if (!listing) return;
      const city = listing.address?.city ?? '';
      const state = listing.address?.state ?? '';
      map[listing.listingAlias] = {
        label: listing.name || listing.listingAlias,
        meta: [city, state].filter(Boolean).join(', '),
      };
    });
    return map;
  }, [listings]);

  // Package name is separate from Spotlight: a listing keeps its plan whether or not it's spotlighted.
  // Today this comes from the fixture-injected field; RECE-1569 swaps in the real advertising data.
  const packageNameById = useMemo(() => {
    const map: Record<string, string> = {};
    listings.forEach((listing) => {
      if (!listing) return;
      const packageName = (listing as AccountViewListingDto & { packageName?: string }).packageName;
      if (packageName) {
        map[listing.listingAlias] = packageName;
      }
    });
    return map;
  }, [listings]);

  // Recomputed every render because advertising ownership arrives through per-row cache queries.
  const managedByFeedIds = new Set<string>();
  listings.forEach((listing) => {
    if (!listing) return;
    if (
      (listing as AccountViewListingDto & { manageInFeed?: boolean }).manageInFeed ||
      isManagedByFeedListing(queryClient, listing.listingAlias)
    ) {
      managedByFeedIds.add(listing.listingAlias);
    }
  });

  const bulkSelectableIds = useMemo(
    () =>
      new Set(
        listings
          .filter((listing): listing is AccountViewListingDto => Boolean(listing))
          .filter((listing) => !listing.isMultifamily)
          .map((listing) => listing.listingAlias),
      ),
    [listings],
  );
  const hasBulkSelectableListings = bulkSelectableIds.size > 0;

  useEffect(() => {
    const presentAliases = new Set(
      listings
        .filter((listing): listing is AccountViewListingDto => Boolean(listing))
        .map((listing) => listing.listingAlias),
    );
    const isStaleSelection = (id: string) => presentAliases.has(id) && !bulkSelectableIds.has(id);

    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (isStaleSelection(id)) {
          changed = true;
          return;
        }
        next.add(id);
      });
      return changed ? next : prev;
    });
    setSelectedPlacementSources((prev) => {
      let changed = false;
      const next = new Map<string, PlacementSource | null>();
      prev.forEach((source, id) => {
        if (isStaleSelection(id)) {
          changed = true;
          return;
        }
        next.set(id, source);
      });
      return changed ? next : prev;
    });
  }, [bulkSelectableIds, listings]);

  // toggleSelectAllOnPage reads advertising eligibility straight from the cache, not via a subscribed query, so
  // nothing re-renders this provider when a row's advertising finishes loading -- force one here instead.
  const [, forceRerenderOnAdvertisingCacheChange] = useState(0);
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        const key = event.query.queryKey;
        if (Array.isArray(key) && typeof key[0] === 'string' && /advertising/i.test(key[0])) {
          forceRerenderOnAdvertisingCacheChange((tick) => tick + 1);
        }
      }),
    [queryClient],
  );

  const vacantIds = useMemo(
    () =>
      new Set(
        listings
          .filter((listing): listing is AccountViewListingDto => Boolean(listing))
          .filter((listing) => FOR_RENT.has(listing.status?.status ?? AccountViewListingStatus.OFF_MARKET))
          .map((listing) => listing.listingAlias),
      ),
    [listings],
  );

  const pinnedCount = spotlightCounts?.pinnedCount ?? overviewData?.pinnedCount ?? 0;
  const autoCount = spotlightCounts?.autoCount ?? overviewData?.autoCount ?? 0;
  // Before the overview returns, seed from the same optimistic/initialCounts source as pinned+auto so the header
  // doesn't flash "0 in Spotlight"; the overview's real value (which also counts feed-managed) takes over once loaded.
  const spotlightedCount = overviewData?.spotlightedCount ?? pinnedCount + autoCount;
  const hasZramManageableListings = overviewData?.hasZramManageableListings ?? true;
  const availableCount = spotlightCounts?.availableCount ?? overviewData?.availableCount ?? 0;
  const totalLicenses = overviewData?.contractedCount ?? RAB_LICENSES;

  const labelFor = (id: string) => labelByAlias[id]?.label ?? id;
  const packageFor = (id: string) => packageNameById[id] ?? COPY.packageNameFallback;

  const toggleSelectionMode = () => {
    setIsSelectionMode((prev) => {
      if (!prev && !hasBulkSelectableListings) {
        return prev;
      }
      if (prev) {
        setSelectedIds(new Set());
        setSelectedPlacementSources(new Map());
      }
      return !prev;
    });
  };

  const toggleSelection = (id: string, placementSource: PlacementSource | null) => {
    if (!bulkSelectableIds.has(id)) {
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setSelectedPlacementSources((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, placementSource);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedPlacementSources(new Map());
    setIsSelectionMode(false);
  };

  const batchMutations = useSpotlightBatchMutations();

  const planBulkTarget = (ids: Array<string>, target: BulkSpotlightTarget) =>
    planBulkSpotlightTransitions(
      smartConfigMode ?? SmartConfigMode.ALL_INCLUDED,
      target,
      ids.map((listingAlias) => ({
        listingAlias,
        managedByFeed: isManagedByFeedListing(queryClient, listingAlias),
        placementSource: selectedPlacementSources.get(listingAlias),
      })),
    );

  const removeSuccessfulSelections = (listingAliases: Array<string>) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      listingAliases.forEach((listingAlias) => next.delete(listingAlias));
      return next;
    });
    setSelectedPlacementSources((prev) => {
      const next = new Map(prev);
      listingAliases.forEach((listingAlias) => next.delete(listingAlias));
      return next;
    });
  };

  const applyBulkTarget = (ids: Array<string>, target: BulkSpotlightTarget) => {
    if (ids.length === 0 || smartConfigMode === null) return;
    const plan = planBulkTarget(ids, target);
    if (plan.unknownSourceCount > 0) return;
    void batchMutations.applyPlan(plan, target, smartConfigMode, removeSuccessfulSelections).then((completed) => {
      if (completed) {
        clearSelection();
      }
      return undefined;
    });
  };

  // Both control cells read pinnedIds and excludedIds whenever the cached placementSource is undefined,
  // which is every row the advertising endpoint returns without a placement. The single-listing actions
  // keep these sets in step through executeMutation's optimistic update; the batch handlers reach the same
  // rows and have to do it too, or a row keeps reading as pinned or excluded until a reload reseeds them.
  // These apply on the aliases the call confirmed rather than optimistically, so a failed row never takes
  // the new state. Nothing is lost by waiting: settleAdvertisingCache writes a defined placementSource for
  // the in-flight window, so the sets are not consulted until the call has settled either way.
  const withoutIds = (prev: Set<string>, ids: Array<string>) => {
    const next = new Set(prev);
    ids.forEach((id) => next.delete(id));
    return next;
  };
  const withIds = (prev: Set<string>, ids: Array<string>) => {
    const next = new Set(prev);
    ids.forEach((id) => next.add(id));
    return next;
  };

  const batchExclude = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchExclude(ids).then((done) => {
      setPinnedIds((prev) => withoutIds(prev, done));
      setExcludedIds((prev) => withIds(prev, done));
    });
    clearSelection();
  };

  const batchInclude = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchInclude(ids).then((done) => {
      setPinnedIds((prev) => withoutIds(prev, done));
      setExcludedIds((prev) => withoutIds(prev, done));
    });
    clearSelection();
  };

  const batchPin = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchPin(ids).then((done) => {
      setExcludedIds((prev) => withoutIds(prev, done));
      setPinnedIds((prev) => withIds(prev, done));
    });
    clearSelection();
  };

  const batchUnexclude = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchUnexclude(ids).then((done) => {
      setExcludedIds((prev) => withoutIds(prev, done));
    });
    clearSelection();
  };

  const batchUninclude = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchUninclude(ids).then((done) => {
      setPinnedIds((prev) => withoutIds(prev, done));
      setExcludedIds((prev) => withoutIds(prev, done));
    });
    clearSelection();
  };

  const batchUnpin = (ids: Array<string>) => {
    if (ids.length === 0) return;
    void batchMutations.batchUnpin(ids).then((done) => {
      setPinnedIds((prev) => withoutIds(prev, done));
    });
    clearSelection();
  };

  // Initialize mutation helpers
  const mutations = useSpotlightMutations({
    labelFor,
    packageFor,
    spotlightCounts,
    setSpotlightCounts,
  });

  // Recomputed every render (not memoized) so it tracks PropertyRow's own disabled gate as advertising loads in.
  const selectableIdsOnPage = [...bulkSelectableIds].filter((id) => isCurrentlySelectable(queryClient, id));
  const isAllOnPageSelected = selectableIdsOnPage.length > 0 && selectableIdsOnPage.every((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () => {
    if (isAllOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableIdsOnPage.forEach((id) => next.delete(id));
        return next;
      });
      setSelectedPlacementSources((prev) => {
        const next = new Map(prev);
        selectableIdsOnPage.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableIdsOnPage.forEach((id) => next.add(id));
      return next;
    });
    setSelectedPlacementSources((prev) => {
      const next = new Map(prev);
      selectableIdsOnPage.forEach((id) => next.set(id, mutations.getCurrentPlacementSource(id) ?? null));
      return next;
    });
  };

  const doPin = async (
    id: string,
    successMessage: string,
    countDeltas?: { pinnedDelta: number; autoDelta: number },
  ) => {
    await mutations.executeMutation({
      id,
      action: 'pin',
      mutationFn: () => mutations.pinMutation(id),
      targetSource: PlacementSource.PINNED,
      countDeltas,
      successMessage,
      errorMessage: `Failed to pin ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        excludedIds: new Set(excludedIds),
      }),
      applyOptimisticUpdate: () => {
        setExcludedIds((prev) => dropFrom(prev, id));
        setPinnedIds((prev) => new Set(prev).add(id));
      },
      restoreSnapshot: (snapshot) => {
        setExcludedIds(snapshot.excludedIds);
        setPinnedIds(snapshot.pinnedIds);
      },
    });
  };

  // The backend engine picks which auto listing frees a slot, so the toast never names one. Before the
  // overview loads the counts read 0/0/0 — pin directly and let the backend decide rather than opening
  // the reassign modal on unknown state.
  const pin = (id: string) => {
    if (pinnedIds.has(id)) {
      return;
    }
    if (!overviewData || mutations.getCurrentPlacementSource(id) === PlacementSource.AUTO || availableCount > 0) {
      doPin(id, `${labelFor(id)} is pinned — it’ll always stay in Spotlight`);
      return;
    }
    if (autoCount > 0) {
      // The backend frees the slot by demoting an auto listing, so auto drops by one alongside the new pin.
      doPin(id, `${labelFor(id)} pinned to Spotlight. An auto-spotlight listing was removed to make room.`, {
        pinnedDelta: 1,
        autoDelta: -1,
      });
      return;
    }
    setReassignCandidate(id);
  };

  const unpin = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'unpin',
      mutationFn: () => mutations.unpinMutation(id),
      targetSource: null,
      successMessage: `${labelFor(id)} returned to auto-optimize`,
      errorMessage: `Failed to unpin ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
      }),
      applyOptimisticUpdate: () => {
        setPinnedIds((prev) => dropFrom(prev, id));
      },
      restoreSnapshot: (snapshot) => {
        setPinnedIds(snapshot.pinnedIds);
      },
    });
  };

  const exclude = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'exclude',
      mutationFn: () => mutations.excludeMutation(id),
      targetSource: PlacementSource.EXCLUDED,
      successMessage: `${labelFor(id)} removed from Spotlight — ${packageFor(id)} only`,
      successTone: 'info',
      errorMessage: `Failed to exclude ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        pinnedIds: new Set(pinnedIds),
        excludedIds: new Set(excludedIds),
      }),
      applyOptimisticUpdate: () => {
        setPinnedIds((prev) => dropFrom(prev, id));
        setExcludedIds((prev) => new Set(prev).add(id));
      },
      restoreSnapshot: (snapshot) => {
        setPinnedIds(snapshot.pinnedIds);
        setExcludedIds(snapshot.excludedIds);
      },
    });
  };

  const unexclude = async (id: string) => {
    await mutations.executeMutation({
      id,
      action: 'unexclude',
      mutationFn: () => mutations.unexcludeMutation(id),
      targetSource: null,
      successMessage: `${labelFor(id)} is back in auto-optimize`,
      errorMessage: `Failed to include ${labelFor(id)}. Try again.`,
      captureSnapshot: () => ({
        excludedIds: new Set(excludedIds),
      }),
      applyOptimisticUpdate: () => {
        setExcludedIds((prev) => dropFrom(prev, id));
      },
      restoreSnapshot: (snapshot) => {
        setExcludedIds(snapshot.excludedIds);
      },
    });
  };

  // RECE-2031: dropping a PINNED override goes through batchUninclude (not unpin -- see applySmartAssign in
  // SmartAssignControlCell), which has no optimistic count update by design (RECE-2014: the freed slot's fate is
  // unknown until the engine responds). The row's own chip is safe to update immediately, but the multi-second
  // gap with zero feedback reads as broken, so this shows a toast right away and forces the table's loading view
  // once that toast clears, until the real response and the properties/overview refetch land.
  const revertPinnedToSmartAssign = async (id: string) => {
    const wasPinned = pinnedIds.has(id);
    mutations.updateAdvertisingCache(id, null);
    // SpotlightControlCell falls back to this set whenever the cached placementSource is undefined, which is
    // every time the advertising refetch lands, because the endpoint does not return that field.
    setPinnedIds((prev) => dropFrom(prev, id));
    enqueueToast(
      <Toast id={`revert-pinned-to-smart-assign-pending-${id}`} tone="info">
        Hang tight — we’re processing your request
      </Toast>,
      { duration: 2000 },
    );
    const shimmerTimer = setTimeout(() => setRevertingPinIds((prev) => new Set(prev).add(id)), 2000);
    try {
      await batchMutations.revertPinnedToSmartAssign(id);
    } catch (error) {
      mutations.updateAdvertisingCache(id, PlacementSource.PINNED);
      if (wasPinned) {
        setPinnedIds((prev) => new Set(prev).add(id));
      }
      enqueueToast(
        <Toast id={`revert-pinned-to-smart-assign-error-${id}`} tone="critical">
          Failed to revert {labelFor(id)}. Try again.
        </Toast>,
      );
      logError(error);
    } finally {
      clearTimeout(shimmerTimer);
      setRevertingPinIds((prev) => dropFrom(prev, id));
    }
  };

  const confirmReassign = async (targetAlias: string) => {
    if (!reassignCandidate) {
      return;
    }
    const incoming = reassignCandidate;

    // Capture snapshot before changes
    const previousPinned = new Set(pinnedIds);
    const previousExcluded = new Set(excludedIds);
    const previousReassignCandidate = reassignCandidate;

    // Optimistic update: swap pinned listings
    setPinnedIds((prev) => {
      const next = new Set(prev);
      next.delete(targetAlias);
      next.add(incoming);
      return next;
    });
    // The displaced listing is removed, not auto-placed: excluded when the account defaults to excluded, returned to
    // Smart Assign when it defaults to included, or simply unspotlighted when Smart Assign is off. AUTO here made it
    // wrongly read as "Spotlight · Smart Assign".
    const displacedSource =
      smartConfigMode === SmartConfigMode.ALL_EXCLUDED
        ? PlacementSource.EXCLUDED
        : smartConfigMode === SmartConfigMode.ALL_INCLUDED
          ? PlacementSource.INCLUDED
          : null;
    setExcludedIds((prev) => {
      const next = dropFrom(prev, incoming);
      return displacedSource === PlacementSource.EXCLUDED ? new Set(next).add(targetAlias) : next;
    });
    mutations.updateAdvertisingCache(incoming, PlacementSource.PINNED);
    mutations.updateAdvertisingCache(targetAlias, displacedSource);
    setReassignCandidate(null);

    try {
      await mutations.reassignMutation({
        postData: { fromListingAlias: targetAlias, toListingAlias: incoming },
      });
      queryClient.invalidateQueries({
        queryKey: [RentalManagerUserPropertySpotlightWebApiHandler_list_Path],
      });
      enqueueToast(
        <Toast id="spotlight-reassigned" tone="success">
          {labelFor(incoming)} pinned to Spotlight. {labelFor(targetAlias)} moved to {packageFor(targetAlias)}.
        </Toast>,
      );
    } catch (error) {
      // Rollback
      setPinnedIds(previousPinned);
      setExcludedIds(previousExcluded);
      mutations.updateAdvertisingCache(incoming, previousExcluded.has(incoming) ? PlacementSource.EXCLUDED : null);
      mutations.updateAdvertisingCache(targetAlias, PlacementSource.PINNED);
      setReassignCandidate(previousReassignCandidate);
      enqueueToast(
        <Toast id="spotlight-reassign-error" tone="critical">
          Failed to reassign Spotlight. Try again.
        </Toast>,
      );
      logError(error);
    }
  };

  // For reassign modal, show all pinned properties as replacement options
  // Use the spotlight list API which returns all spotlighted listings
  const reassignOccupants: Array<SpotlightOccupant> = useMemo(() => {
    // Only compute when modal is actually open and data is loaded
    if (!reassignCandidate || !spotlightedListings?.listings) {
      return [];
    }

    // Map the spotlighted listings to occupants format
    return spotlightedListings.listings.map((listing) => {
      const city = listing.address?.city ?? '';
      const state = listing.address?.state ?? '';
      const { formattedStreet } = propertyUtils.formatAddress(listing.address ?? {});
      return {
        alias: listing.listingAlias,
        label: formattedStreet || listing.listingAlias,
        meta: [city, state].filter(Boolean).join(', '),
        origin: 'pinned' as const,
      };
    });
  }, [reassignCandidate, spotlightedListings]);

  // The backend resets every non-PINNED row, so the invalidated overview refetch is the only count update.
  // Stays open until the mutation settles so the confirm button can guard against a double submit.
  const confirmDisableSmartAssign = () => {
    void saveSmartConfigMode({ postData: { autoOptimizeEnabled: false } })
      .then(() => {
        setIsDisableSmartAssignRequested(false);
        enqueueToast(
          <Toast id="smart-assign-disabled" tone="success">
            Smart Assign is off. You’ll apply Listing Spotlight to each listing yourself.
          </Toast>,
        );
        return;
      })
      .catch((error: Error) => {
        setIsDisableSmartAssignRequested(false);
        enqueueToast(
          <Toast id="smart-assign-disable-error" tone="critical">
            {error.message || 'Smart Assign could not be turned off. Try again.'}
          </Toast>,
        );
        return;
      });
  };

  const value: AutoSpotlightContextValue = {
    active,
    applyBulkTarget,
    batchExclude,
    batchInclude,
    batchPin,
    batchUnexclude,
    batchUninclude,
    batchUnpin,
    clearSelection,
    exclude,
    excludedIds,
    managedByFeedIds,
    isOverviewLoaded: Boolean(overviewData),
    isSelectionMode,
    isAllOnPageSelected,
    selectedIds,
    toggleSelection,
    toggleSelectionMode,
    toggleSelectAllOnPage,
    licenses: totalLicenses,
    pinnedCount,
    autoCount,
    spotlightedCount,
    hasZramManageableListings,
    hasBulkSelectableListings,
    availableCount,
    onChangeOptimizer: (id) => {
      const previousOverride = userOverride;
      setUserOverride(id);
      void saveOptimizationMode({
        postData: {
          optimizationMode: id === 'rent' ? OptimizationMode.RENT : OptimizationMode.DOM,
        },
      })
        .then(() => {
          enqueueToast(
            <Toast id="spotlight-optimizer-saved" tone="success">
              Optimize for updated to {id === 'rent' ? 'Rent' : 'Days on market'}.
            </Toast>,
          );
          return;
        })
        .catch(() => {
          // Snap the control back to the last persisted mode — the server rejected this change.
          setUserOverride(previousOverride);
          enqueueToast(
            <Toast id="spotlight-optimizer-error" tone="critical">
              Optimize for setting could not be saved. Try again.
            </Toast>,
          );
          return;
        });
    },
    optimizer,
    smartConfigMode,
    isDisableSmartAssignRequested,
    isDisablingSmartAssign,
    requestDisableSmartAssign: () => setIsDisableSmartAssignRequested(true),
    cancelDisableSmartAssign: () => setIsDisableSmartAssignRequested(false),
    confirmDisableSmartAssign,
    packageNameById,
    pin,
    planBulkTarget,
    pinnedIds,
    reassignCandidate,
    reassignCandidateLabel: reassignCandidate ? labelFor(reassignCandidate) : '',
    isReassignOccupantsLoading,
    reassignOccupants,
    cancelReassign: () => setReassignCandidate(null),
    confirmReassign,
    setReassignCandidate,
    isRevertingPinPending: revertingPinIds.size > 0,
    isTableLocked,
    revertPinnedToSmartAssign,
    unexclude,
    unpin,
    vacantIds,
  };

  return (
    <AutoSpotlightContext.Provider value={value}>
      {children}
      <SpotlightReassignModal />
      <SmartAssignDisableModal />
    </AutoSpotlightContext.Provider>
  );
}
